/**
 * @file BadActorStore.js
 * @description This module provides functions to manage bad actor data and feature flags.
 * It uses a session cache to store and retrieve data, and integrates with the zion feature flag service.
 */
import { getFeatureFlag, flagsAreReady } from '@fs/zion-flags'
import Cache from '@fs/zion-cache'
import zionDebug from '@fs/zion-debug'
import { badActorFlag as mockFlag } from '../../../dev.flags'

const debug = zionDebug('rip:badActorStore')
/**
 * @constant {string} storeName - The name of the store used in the cache.
 * @constant {string} flagName - The name of the feature flag.
 * @constant {Cache} sessionCache - The session cache instance.
 */
const storeName = 'badActorStore'
const flagName = 'give_bad_actor'
const sessionCache = new Cache({ storeName, type: 'session' })
window.badActorStore = sessionCache

export const deserialize = (data) => {
  try {
    return JSON.parse(data)
  } catch (e) {
    return data
  }
}

/**
 * @function getBadActorFlag
 * @description Retrieves the bad actor feature flag configuration.
 * @returns {Promise<Object>} The data, including the configuration object for the bad actor feature flag.
 */
export const getBadActorFlag = async () => {
  try {
    await flagsAreReady()
    const flag = await getFeatureFlag(flagName)
    debug('getBadActorFlag:flag', flag)
    const localFlags = deserialize(localStorage.getItem('fsZionFlags'))
    debug('getBadActorFlag:localFlags', localFlags)
    const flagOverride = localFlags?.[flagName]
    debug('getBadActorFlag:flagOverride', flagOverride)
    if (flagOverride) {
      return { ...mockFlag, isOn: flagOverride?.treatment === 'on' }
    }
    return { ...flag, isOn: flag?.treatment === 'on' }
  } catch (error) {
    // eat the error and return the mock flag
    debug('getBadActorFlag:error', error)
    return { ...mockFlag, isOn: mockFlag.treatment === 'on' }
  }
}

export const getBadActorFlagKey = async (key) => {
  const { config } = await getBadActorFlag()
  return config?.[key]
}

/**
 * @function getBadActorData
 * @description Retrieves the bad actor data for a given key.
 * @param {string} key - The key for the bad actor data.
 * @returns {Promise<Object>} The bad actor data for the given key.
 */
export const getBadActorData = async (key) => {
  const { config } = await getBadActorFlag()
  const cachedData = (await sessionCache.getItem(key)) || {}
  const data = {
    enabled: config?.[key]?.enabled ?? false, // default to false if not defined
    pass: cachedData?.pass ?? true, // default to true if not defined
    ...(cachedData?.count !== undefined && { count: cachedData?.count || 0 }),
    ...(config?.[key]?.threshhold !== undefined && { threshhold: config[key].threshhold || 0 }),
    ...(cachedData?.originalTerm && { originalTerm: cachedData.originalTerm }),
    ...(cachedData?.editedTerm && { editedTerm: cachedData.editedTerm }),
  }
  return data
}

/**
 * Retrieves all bad actor data by combining flag data and cached data.
 *
 * @async
 * @function getAllBadActorData
 * @returns {Promise<Object>} A promise that resolves to an object containing the combined bad actor data.
 */
export const getAllBadActorData = async () => {
  const { config } = await getBadActorFlag()
  const keyArray = Object.keys(config)
  const keyData = await Promise.all(keyArray.map(async (key) => ({ [key]: await getBadActorData(key) })))
  const object = keyData.reduce((acc, curr) => ({ ...acc, ...curr }), {})
  return object
}

/**
 * @function setBadActorData
 * @description Sets the bad actor data for a given key.
 * @param {string} key - The key for the bad actor data.
 * @param {Object} data - The data to set for the key. Note, partials are supported.
 * @returns {Promise<void>}
 */
export const setBadActorData = async (key, data) => {
  const cachedData = (await sessionCache.getItem(key)) || {}
  const keyedData = { ...cachedData, ...data }
  return sessionCache.setItem(key, keyedData)
}

/**
 * @function clearBadActorData
 * @description Clears all bad actor data from the session cache.
 * @returns {Promise<void>}
 */
export const clearBadActorData = async () => {
  return sessionCache.clear()
}
